import get from 'lodash/get';
import { REGISTRATION_METHOD_TICKETS } from 'common/constants/registrationMethods';
import { getEventId } from 'common/store/events/selectors';
import { TYPE_VIRTUAL_EVENT_MULTISESSION } from 'common/constants/virtualEvent';
import { getEntitySelector } from '../selectors';
import { ENTITIES_KEY_EVENT } from './index';

export const getAllEventIds = (state) => getEntitySelector(state, ENTITIES_KEY_EVENT).allIds;

export const getEventsFromIds = (state, ids) =>
    ids ? ids.map((eventId) => getEntitySelector(state, ENTITIES_KEY_EVENT)[eventId]) : [];

export const getAllEvents = (state) => getEventsFromIds(state, getEntitySelector(state, ENTITIES_KEY_EVENT).allIds);

export const getEvent = (state, eventId) => getEntitySelector(state, ENTITIES_KEY_EVENT)[eventId];

export const getIsTicketedEvent = (state, eventId) => {
    const event = getEvent(state, eventId);
    return !!(event && event.rsvpMethod === REGISTRATION_METHOD_TICKETS);
};

export const getIsHubEvent = (state, eventId) => {
    const event = getEvent(state, eventId);
    return event?.isHub;
};

export const getCurrentTicketId = (state) => get(state, 'ticketSettings.editTicketTypeId', null);

/**
 * Data for the event in entities can unfortunately vary, when its populated from
 * different bootstrap/window data. As of 2018-10-09:
 *
 * Splash 4 has a string value for the 'domain' field on an event.
 * App in the old CMS (Workspace entry) has an object value for the 'domain' field.
 *
 * If you're editing this later, and that's no longer true, feel free to
 * simplify/remove this function!
 */
export const getEventSubDomain = (state, eventId) => {
    const event = getEvent(state, eventId);

    return get(event, 'domain.subdomain') || get(event, 'domain');
};

export const getEventPreviewUrl = (state, eventId) => {
    const event = getEvent(state, eventId);

    return event.previewUrl || '';
};

/**
 * Returns undefined in case getEventSubdomain or getEventId return an error.
 * This solves the problem of rendering FormBuilder in the forms dashboards
 * and having a crash because there's no APP namespace.
 */
export const getEventSubdomainIfAny = (state) => {
    try {
        return getEventSubDomain(state, getEventId(state));
    } catch (err) {
        return undefined;
    }
};

/**
 * @param {Object} state
 * @param {string} eventId
 * @return {string}
 */
export const getEventRsvpMethod = (state, eventId) => {
    const event = getEvent(state, eventId);
    return get(event, 'rsvpMethod');
};

/**
 * @param {Object} state
 * @param {string} eventId
 * @return {boolean}
 */
export const getAutosave = (state, eventId) => {
    const event = getEvent(state, eventId);
    return get(event, 'autosave');
};

export const getEventOrgId = (state, eventId) => {
    const event = getEvent(state, eventId);
    return get(event, 'organization.id');
};

/**
 * Returns if an event is multisession
 */
export const getIsMultisessionEvent = (state) => {
    const event = getEvent(state, getEventId(state));
    return !!(event && get(event, 'multisession') !== '0');
};

/**
 * Returns if an event is using Sessions Legacy
 */
export const getIsSessionLegacyEvent = (state) => {
    const event = getEvent(state, getEventId(state));
    return !!(event && get(event, 'multisession') === '1');
};

/**
 * Returns if an event is using Sessions Virtual
 */
export const getIsSessionVirtualEvent = (state) => {
    const event = getEvent(state, getEventId(state));
    return !!(event && get(event, 'multisession') === TYPE_VIRTUAL_EVENT_MULTISESSION);
};

/**
 * Returns if an event can have updated responses
 */
export const getIsRegistrationUpdatingEnabled = (state) => {
    const event = getEvent(state, getEventId(state));
    return !!get(event, 'isRegistrationUpdatingEnabled');
};

/**
 * Returns the event's currency code
 */
export const getCurrency = (state) => {
    const event = getEvent(state, getEventId(state));
    return get(event, 'currency');
};

/**
 * Returns if an event has taxes
 */
export const getHasTaxes = (state) => {
    const event = getEvent(state, getEventId(state));
    return !!get(event, 'hasTaxes');
};

/**
 * Returns if an event has stripe payout method
 */
export const getIsStripePayoutMethod = (state) => {
    const event = getEvent(state, getEventId(state));
    return !!get(event, 'isStripePayoutMethod');
};
